'use client';

import { useCallback, useEffect, useState } from 'react';

import {
  ScheduleSlotsDay,
  groupSlotsByDate,
} from '@/app/(main)/(pages)/_components/schedules/_utils/group-slots-by-date';
import { getSchedulesSlotById } from '@/app/(main)/(pages)/_components/schedules/_services/get-schedules-slot-by-id.service';
import { SearchApiDayTime } from '@/app/(main)/(pages)/_types/search-api-response.type';

export type { ScheduleSlotsDay };

export interface ScheduleSlotFilters {
  dayTime?: SearchApiDayTime;
  minDate?: string;
  maxDate?: string;
}

export interface UseScheduleSlotsParams {
  scheduleId: string | undefined;
  limitDays: number;
  filters: ScheduleSlotFilters;
}

export interface UseScheduleSlotsReturn {
  isLoading: boolean;
  days: ScheduleSlotsDay[];
  hasMore: boolean;
  offset: number;
  goNext(): void;
  goPrev(): void;
}

export function useScheduleSlots({
  scheduleId,
  limitDays,
  filters,
}: UseScheduleSlotsParams): UseScheduleSlotsReturn {
  const { dayTime, minDate, maxDate } = filters;

  const [days, setDays] = useState<ScheduleSlotsDay[]>([]);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(Boolean(scheduleId));
  const [hasMore, setHasMore] = useState(true);
  const [prevScheduleId, setPrevScheduleId] = useState(scheduleId);
  const [prevLimitDays, setPrevLimitDays] = useState(limitDays);
  const [prevDayTime, setPrevDayTime] = useState(dayTime);
  const [prevMinDate, setPrevMinDate] = useState(minDate);
  const [prevMaxDate, setPrevMaxDate] = useState(maxDate);

  if (
    scheduleId !== prevScheduleId
    || limitDays !== prevLimitDays
    || dayTime !== prevDayTime
    || minDate !== prevMinDate
    || maxDate !== prevMaxDate
  ) {
    setPrevScheduleId(scheduleId);
    setPrevLimitDays(limitDays);
    setPrevDayTime(dayTime);
    setPrevMinDate(minDate);
    setPrevMaxDate(maxDate);
    setOffset(0);
    setDays([]);
    setHasMore(true);
    setIsLoading(Boolean(scheduleId));
  }

  useEffect(() => {
    if (!scheduleId) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    getSchedulesSlotById({
      id: scheduleId, limitDays, offsetDays: offset, dayTime, minDate, maxDate,
    })
      .then((result) => {
        if (cancelled) {
          return;
        }
        setDays(groupSlotsByDate(result.slots));
        setHasMore(result.hasMore);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setDays([]);
        setHasMore(false);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [scheduleId, limitDays, offset, dayTime, minDate, maxDate]);

  const goNext = useCallback(() => {
    if (!hasMore) {
      return;
    }
    setOffset((current) => current + limitDays);
  }, [hasMore, limitDays]);

  const goPrev = useCallback(() => {
    setOffset((current) => Math.max(0, current - limitDays));
  }, [limitDays]);

  return { isLoading, days, hasMore, offset, goNext, goPrev };
}
